import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { escalaFirma, aBase, aPantalla, type Trazo } from '../../lib/firma-trazos';

// Firma a mano en un canvas, con mouse, dedo o lápiz (Pointer Events), sin
// librerías. Fondo blanco y tinta oscura, como en papel; se exporta en PNG.
// El trazo se guarda como puntos en el ancho donde se empezó a firmar: si el
// ancho cambia (girar el celular) se redibuja sin perderlo (bloque 5c, §17 ítem 86).
export interface SignaturePadHandle {
  clear:   () => void;
  isEmpty: () => boolean;
  toBlob:  () => Promise<Blob | null>;
}

interface Props {
  height?:   number;
  disabled?: boolean;
  onChange?: (vacio: boolean) => void;
}

const SignaturePad = forwardRef<SignaturePadHandle, Props>(function SignaturePad({ height = 160, disabled, onChange }, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dibujando = useRef(false);
  const trazos    = useRef<Trazo[]>([]);
  const anchoBase = useRef(0);
  const [vacio, setVacio] = useState(true);

  const escala = () => escalaFirma(anchoBase.current, canvasRef.current?.offsetWidth ?? 0);

  const redibujar = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const ancho = canvas.offsetWidth;
    if (ancho <= 0) return;
    canvas.width  = Math.round(ancho * ratio);
    canvas.height = Math.round(height * ratio);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, ancho, height);
    ctx.lineWidth   = 2.2;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
    ctx.strokeStyle = '#0f172a';
    const s = escalaFirma(anchoBase.current, ancho);
    trazos.current.forEach(t => {
      if (t.length === 0) return;
      const pts = t.map(p => aPantalla(p, s));
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      if (pts.length === 1) ctx.lineTo(pts[0].x + 0.1, pts[0].y);
      pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    });
  }, [height]);

  useEffect(() => {
    redibujar();
    const canvas = canvasRef.current;
    if (!canvas) return;
    let anchoPrevio = canvas.offsetWidth;
    const alCambiar = () => {
      const ancho = canvas.offsetWidth;
      if (ancho !== anchoPrevio) { anchoPrevio = ancho; redibujar(); }
    };
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(alCambiar);
      ro.observe(canvas);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', alCambiar);
    return () => window.removeEventListener('resize', alCambiar);
  }, [redibujar]);

  const marcarVacio = useCallback((v: boolean) => {
    setVacio(v);
    onChange?.(v);
  }, [onChange]);

  const limpiar = useCallback(() => {
    trazos.current = [];
    anchoBase.current = 0;
    redibujar();
    marcarVacio(true);
  }, [redibujar, marcarVacio]);

  useImperativeHandle(ref, () => ({
    clear: limpiar,
    isEmpty: () => vacio,
    toBlob: () => new Promise(resolve => {
      const canvas = canvasRef.current;
      if (!canvas || vacio) { resolve(null); return; }
      canvas.toBlob(b => resolve(b), 'image/png');
    }),
  }), [limpiar, vacio]);

  function punto(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function onDown(e: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dibujando.current = true;
    if (trazos.current.length === 0) anchoBase.current = e.currentTarget.offsetWidth;
    trazos.current.push([aBase(punto(e), escala(), height)]);
  }

  function onMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!dibujando.current || disabled) return;
    const ctx = e.currentTarget.getContext('2d');
    const actual = trazos.current[trazos.current.length - 1];
    const ultimo = actual?.[actual.length - 1];
    if (!ctx || !actual || !ultimo) return;
    const s = escala();
    const desde = aPantalla(ultimo, s);
    const p = punto(e);
    ctx.beginPath();
    ctx.moveTo(desde.x, desde.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    actual.push(aBase(p, s, height));
    if (vacio) marcarVacio(false);
  }

  function onUp() {
    dibujando.current = false;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', minWidth: 0 }}>
      <canvas
        ref={canvasRef}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onPointerLeave={onUp}
        aria-label="Área de firma"
        style={{
          display: 'block', width: '100%', height, borderRadius: 8, border: '1px solid #334155', boxSizing: 'border-box',
          background: '#ffffff', touchAction: 'none', cursor: disabled ? 'not-allowed' : 'crosshair',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ color: '#64748b', fontSize: '0.72rem' }}>Firme con el mouse o con el dedo</span>
        <button
          type="button"
          className="ss-tap"
          onClick={limpiar}
          disabled={disabled || vacio}
          style={{ background: 'none', border: 'none', color: vacio ? '#334155' : '#94a3b8', cursor: vacio ? 'default' : 'pointer', fontSize: '0.76rem', flexShrink: 0 }}
        >
          Borrar firma
        </button>
      </div>
    </div>
  );
});

export default SignaturePad;
